<template>
	<div class="page onboarding-page">
		
		<aside class="onboarding-header pad-x">
			<div class="contain">
				<Logo no-link />
				
				<div class="onboarding-header-step" v-if="title">
					<div class="fs-14 lh-18 md-fs-16 md-lh-22 color-violet fw-700">{{ title }}</div>
					<div class="fs-14 lh-18 md-fs-16 md-lh-20 mt-4" v-if="showGroupNumbers">
						Step {{ currentGroupNumber }} of {{ lastGroupNumber }}
					</div>
				</div>
				
				<div v-if="showActions" class="onboarding-header-actions">
					<button v-if="showExitButton" class="btn" @click="$logout">Exit</button>
				</div>
			</div>
			<div v-if="progress != null" class="onboarding-header-progress">
				<div class="onboarding-header-progress-bar" :style="`width: ${(progress * 100).toFixed(2)}%`"></div>
			</div>
			<CheckboxField v-if="showDebugOption" v-model="debug" class="abs right-12 top-12">Debug</CheckboxField>
		</aside>

		<router-view />

		<Modal v-model="showIncompleteProfileModal" class="onboarding-message-modal" no-close-button>
			<template #title>Welcome Back!</template>
			<template #content>
				<svg width="69" height="69" viewBox="0 0 69 69" xmlns="http://www.w3.org/2000/svg">
					<defs>
						<linearGradient id="wbgr1" x1="15.04" y1="75.109" x2="33.347" y2="-33.598" gradientUnits="userSpaceOnUse">
							<stop offset=".089" stop-color="#BE52FE" stop-opacity="0" />
							<stop offset=".99" stop-color="#BE52FE" />
						</linearGradient>
						<linearGradient id="wbgr2" x1="23.52" y1="39.084" x2="23.945" y2="26.476" gradientUnits="userSpaceOnUse">
							<stop offset=".089" stop-color="#BE52FE" stop-opacity="0" />
							<stop offset=".99" stop-color="#BE52FE" />
						</linearGradient>
					</defs>
					<path d="M12.245 47.706a25.864 25.864 0 1 1 9.049 9.05h0l-8.937 2.553a2.156 2.156 0 0 1-2.666-2.666l2.554-8.937h0Z" fill="none" stroke="#00C7C9" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" />
					<path d="M34.5 37.734a3.234 3.234 0 1 0 0-6.468 3.234 3.234 0 0 0 0 6.468ZM21.563 37.734a3.234 3.234 0 1 0 0-6.468 3.234 3.234 0 0 0 0 6.468ZM47.438 37.734a3.234 3.234 0 1 0 0-6.468 3.234 3.234 0 0 0 0 6.468Z" fill="#00C7C9" />
					<path d="M35.93 11.474A23.082 23.082 0 0 0 14.617 46.25c.398.65.524 1.452.304 2.22l-2.244 7.854 7.853-2.244a2.782 2.782 0 0 1 2.219.303A23.082 23.082 0 1 0 35.93 11.474ZM20.94 59.75A28.647 28.647 0 1 0 9.25 48.06L7.016 55.88a4.938 4.938 0 0 0 6.105 6.105l7.819-2.234Zm-9.347-3.117Zm.774.773v.001l-.602-.172.602.172Z" clip-rule="evenodd" fill="#wbgr1" fill-rule="evenodd" />
					<path d="M21.563 37.734a3.234 3.234 0 1 0 0-6.468 3.234 3.234 0 0 0 0 6.468Zm12.937 0a3.234 3.234 0 1 0 0-6.468 3.234 3.234 0 0 0 0 6.468ZM50.672 34.5a3.234 3.234 0 1 1-6.469 0 3.234 3.234 0 0 1 6.469 0Z" clip-rule="evenodd" fill="#wbgr2" fill-rule="evenodd" />
				</svg>
				<p>
					Looks like you still have a few tasks to finish before your profile is complete. This is a
					critical step for
					<template v-if="plan && !plan.canBeHired">finding help for your events.</template>
					<template v-else>getting discovered and being offered a job.</template>
				</p>
				<p>
					<BaseButton class="button-primary" @click="showIncompleteProfileModal = false">
						Continue Profile Setup
					</BaseButton>
				</p>
			</template>
		</Modal>

		<!-- Portfolio upload progress widget -->
		<PortfolioUploadProgress />

		<!-- Sync calendar modal -->
		<SyncCalendar />
	</div>
</template>

<script>
import layout from '@/mixins/layout';
import onboarding from '@/mixins/onboarding';
import SyncCalendar from '@/components/calendars/SyncCalendar';

export default {
  name: 'OnboardingLayout',
  mixins: [layout, onboarding],
  components: { SyncCalendar },
  data() {
    return {
      showIncompleteProfileModal: false,
    };
  },
  computed: {
    currentGroupNumber() {
      return this.getGroupNumber(this.currentStep);
    },
    lastGroupNumber() {
      /** @type {import('@/mixins/onboarding').OnboardingComponent} */
      const { steps } = this;
      return this.getGroupNumber(steps[steps.length - 1]);
    },
    progress() {
      const { currentStepIndex, steps } = this;
      let count;
      return currentStepIndex != null && (count = steps.length) >= 0
        ? (currentStepIndex + 1) / count
        : null;
    },
    rootClass() {
      return 'onboarding';
    },
    showActions() {
      return this.showExitButton;
    },
    showDebugOption() {
      return process.env.NODE_ENV === 'local' && localStorage.getItem('onboarding/debug') != null;
    },
    showExitButton() {
      return !!this.$store.getters['auth/isImpersonated'];
    },
    showGroupNumbers() {
      const { currentGroupNumber } = this;
      return !!(currentGroupNumber && currentGroupNumber <= this.lastGroupNumber);
    },
    title() {
      /** @type {import('@/helpers/onboarding').OnboardingStep | null} */
      const step = this.currentStep;
      return (step && step.title) || '';
    },
  },
  methods: {
    /**
     * @param {import('@/helpers/onboarding').OnboardingStep | null | undefined} step
     * @returns {number}
     */
    getGroupNumber(step) {
      return step ? step.groupIndex + 1 : 0;
    },
  },
  mounted() {
    if (this.isIncompleteProfileRedirect && this.$route.name !== 'onboardingAvailability') {
      this.showIncompleteProfileModal = true;
    }
  },
  watch: {
    $route: {
      /**
       * @param {import('vue-router').Route | null | undefined} route
       * @returns {void}
       */
      handler(route) {
        if (route) {
          this.$store.commit('onboarding/setCurrentRouteName', route.name);
        }
      },
      immediate: true,
    },
  },
  metaInfo() {
    return {
      title: this.title,
    };
  },
};
</script>
